const KRATOS_URL = window._env_.REACT_APP_KRATOS_URL || "http://localhost:4433";

export interface KratosFlow {
    id: string;
    ui: {
        nodes: KratosNode[];
        action: string;
        method: string;
    };
}

interface KratosNode {
    type: string;
    group: string;
    attributes: {
        name: string;
        value?: string;
        type?: string;
        node_type?: string;
    };
}

export interface KratosSubmitResponse {
    session?: Record<string, unknown>;
    redirect_browser_to?: string;
}

interface KratosMessage {
    id: number | string;
    text: string;
    type: string;
}

export class KratosError extends Error {
    public readonly messageIds: (number | string)[];

    constructor(messages: KratosMessage[]) {
        super(messages.map((m) => m.text).join(". "));
        this.messageIds = messages.map((m) => m.id);
    }
}

const extractFlowMessages = (body: Record<string, unknown>): KratosMessage[] => {
    const ui = body.ui as { nodes?: KratosNode[]; messages?: KratosMessage[] } | undefined;
    if (!ui) return [];

    // Flow-level messages (e.g. "account already exists")
    const flowMessages = (ui.messages ?? []).filter((m) => m.type === "error");

    // Node-level messages (e.g. "password found in data breaches")
    const nodeMessages = (ui.nodes ?? [])
        .flatMap((node) => (node as unknown as { messages: KratosMessage[] }).messages ?? [])
        .filter((m) => m.type === "error");

    return [...flowMessages, ...nodeMessages];
};

const kratosRequest = async <T>(
    path: string,
    options: RequestInit = {},
): Promise<T> => {
    const response = await fetch(`${KRATOS_URL}${path}`, {
        credentials: "include",
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
        },
        ...options,
    });

    if (!response.ok) {
        const body = await response.json().catch(() => ({}));

        // Kratos returns 422 with redirect_browser_to for OIDC flows
        if (response.status === 422 && body.redirect_browser_to) {
            return body as T;
        }

        // Extract Kratos flow error message IDs (400)
        if (response.status === 400 && body.ui) {
            const messages = extractFlowMessages(body);
            if (messages.length) {
                throw new KratosError(messages);
            }
        }

        // Kratos API errors (e.g. session_already_available)
        const errorId = (body.error as { id?: string })?.id;
        if (errorId) {
            throw new KratosError([{ id: errorId as unknown as number, text: (body.error as { message?: string })?.message || "", type: "error" }]);
        }

        throw new Error(
            body.error?.message || body.message || `Kratos request failed: ${response.status}`,
        );
    }

    return response.json();
};

export const extractCsrfToken = (flow: KratosFlow): string => {
    const csrfNode = flow.ui.nodes.find(
        (node) =>
            node.attributes.name === "csrf_token" &&
            node.group === "default",
    );
    if (!csrfNode?.attributes.value) {
        throw new Error("CSRF token not found in Kratos flow");
    }
    return csrfNode.attributes.value;
};

export const initLoginFlow = async (
    returnTo?: string,
): Promise<KratosFlow> => {
    const params = new URLSearchParams();
    if (returnTo) {
        params.set("return_to", returnTo);
    }
    const query = params.toString() ? `?${params.toString()}` : "";
    return kratosRequest<KratosFlow>(
        `/self-service/login/browser${query}`,
    );
};

export const initRegistrationFlow = async (
    returnTo?: string,
): Promise<KratosFlow> => {
    const params = new URLSearchParams();
    if (returnTo) {
        params.set("return_to", returnTo);
    }
    const query = params.toString() ? `?${params.toString()}` : "";
    return kratosRequest<KratosFlow>(
        `/self-service/registration/browser${query}`,
    );
};

export const submitPasswordLogin = async (
    flowId: string,
    email: string,
    password: string,
    csrfToken: string,
): Promise<KratosSubmitResponse> => {
    return kratosRequest<KratosSubmitResponse>(
        `/self-service/login?flow=${flowId}`,
        {
            method: "POST",
            body: JSON.stringify({
                method: "password",
                identifier: email,
                password,
                csrf_token: csrfToken,
            }),
        },
    );
};

export const submitPasswordRegistration = async (
    flowId: string,
    email: string,
    password: string,
    csrfToken: string,
): Promise<KratosSubmitResponse> => {
    return kratosRequest<KratosSubmitResponse>(
        `/self-service/registration?flow=${flowId}`,
        {
            method: "POST",
            body: JSON.stringify({
                method: "password",
                traits: { email },
                password,
                csrf_token: csrfToken,
            }),
        },
    );
};

export const submitOidcFlow = async (
    flowId: string,
    provider: string,
    csrfToken: string,
    flowType: "login" | "registration" = "login",
): Promise<KratosSubmitResponse> => {
    return kratosRequest<KratosSubmitResponse>(
        `/self-service/${flowType}?flow=${flowId}`,
        {
            method: "POST",
            body: JSON.stringify({
                method: "oidc",
                provider,
                csrf_token: csrfToken,
            }),
        },
    );
};
